/**
 * Converts the values a grid is filtered by into the value shape a form dropdown stores.
 *
 * Only pick-from-a-list widgets take part: prefilling a free text or date field from a filter
 * would be meaningless. Each widget kind stores its selection differently, so the conversion is
 * driven by the widget, which is the only place that knows both the shape and whether it is multi.
 */

import { FORM_WIDGET_COMPONENTS } from './formConstants';

type WidgetOption = { id?: unknown; label?: string };

export type WidgetDefaultParams = {
    component: string;
    /** Values the view is filtered by for this field */
    values: string[];
    multiple?: boolean;
    options?: WidgetOption[];
};

/** Widgets storing the label(s) the user picked. */
const LABEL_VALUE_COMPONENTS = new Set<string>([FORM_WIDGET_COMPONENTS.metadataAutocomplete]);

/**
 * Reference widgets (reference / entity autocomplete) do not take part: a filter only carries a
 * label, while a reference field needs a resolved entity id to select an option and to save.
 * Prefilling them needs a label to reference lookup, which is a separate capability.
 */

/** Widgets storing the id of a known option, so a filter value has to match one of them. */
const OPTION_ID_COMPONENTS = new Set<string>([
    FORM_WIDGET_COMPONENTS.autocomplete,
    FORM_WIDGET_COMPONENTS.radiogroup,
    FORM_WIDGET_COMPONENTS.toggleButtonGroup
]);

/** Widgets storing the whole option object. */
const OPTION_OBJECT_COMPONENTS = new Set<string>([FORM_WIDGET_COMPONENTS.chips]);

export function isPrefillableSelectWidget(component: string | undefined): boolean {
    return (
        LABEL_VALUE_COMPONENTS.has(component) ||
        OPTION_ID_COMPONENTS.has(component) ||
        OPTION_OBJECT_COMPONENTS.has(component)
    );
}

function findOptionByLabel(options: WidgetOption[], value: string): WidgetOption | undefined {
    const target = value.trim().toLowerCase();
    return options.find(
        (option) =>
            String(option?.label ?? '')
                .trim()
                .toLowerCase() === target || String(option?.id ?? '').toLowerCase() === target
    );
}

/**
 * Resolves the value to prefill, or `undefined` when the filters cannot fill this field:
 * a single-value field needs exactly one active value, and an option-backed field needs the
 * filter value to match one of its options.
 */
export function toWidgetDefaultValue({
    component,
    multiple = false,
    options = [],
    values
}: WidgetDefaultParams): { value: unknown } | undefined {
    if (values.length === 0 || (!multiple && values.length !== 1)) {
        return undefined;
    }

    if (LABEL_VALUE_COMPONENTS.has(component)) {
        return { value: multiple ? values : values[0] };
    }

    const matched = values.map((value) => findOptionByLabel(options, value)).filter(Boolean);
    if (matched.length !== values.length) {
        return undefined;
    }

    if (OPTION_OBJECT_COMPONENTS.has(component)) {
        return { value: multiple ? matched : matched[0] };
    }

    if (OPTION_ID_COMPONENTS.has(component)) {
        // Multi option widgets keep whole options, single ones keep just the id.
        return { value: multiple ? matched : matched[0].id };
    }

    return undefined;
}
